import type { Wallet } from "@/types";

export type ValuationStatus =
  | "VALUED"
  | "PARTIAL"
  | "UNVALUED"
  | "STALE"
  | "UNAVAILABLE"
  | "UNKNOWN";

export interface GatewayValuation {
  status: ValuationStatus;
  valueUsd: string | null;
  asOf: string | null;
  source: string | null;
  reasonCode: string | null;
}

export interface GatewayWalletValuation extends GatewayValuation {
  walletId: string;
}

export interface ValuationSummary {
  valuedTotalUsd: string | null;
  /** Gateway-sourced display valuation; never a synthetic or zero-filled quote. */
  referenceTotalUsd?: string | null;
  valuedWalletCount: number;
  unvaluedWalletCount: number;
  staleWalletCount: number;
  asOf: string | null;
  status: ValuationStatus;
}

export const UNKNOWN_VALUE_LABEL = "—";

export const usdCompact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const usdStandard = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const usdPrecise = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Prefer valued USD, otherwise the measured reference USD authorised for Dev display. */
export function displayTotal(summary: ValuationSummary): { value: string | null; reference: boolean } {
  return { value: summary.valuedTotalUsd ?? summary.referenceTotalUsd ?? null, reference: false };
}

export function parseUsdValue(value: string | number | null | undefined): number | null {
  if (typeof value === "number") {
    // `+ 0` turns a negative zero into a plain zero: a balance of -0 is not a debt.
    return Number.isFinite(value) ? value + 0 : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  // Number("") and Number("   ") are both 0, which would render an absent
  // valuation as a confident $0. A blank string carries no number at all.
  if (value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed + 0 : null;
}

/** Values at or above this keep their thousands separators rather than a compact suffix. */
const GROUPED_FORMAT_THRESHOLD = 1e9;

export function formatUsdValue(
  value: string | number | null | undefined,
  formatter: Intl.NumberFormat = usdCompact,
): string {
  const parsed = parseUsdValue(value);
  if (parsed === null) {
    return UNKNOWN_VALUE_LABEL;
  }
  // Compact notation renders $1,234,567,890 as "$1.2B" and drops the digits a
  // treasury total is read for, so nine figures and up are grouped instead.
  const effective = formatter === usdCompact && Math.abs(parsed) >= GROUPED_FORMAT_THRESHOLD
    ? usdStandard
    : formatter;
  return effective.format(parsed);
}

const CENTS_PER_USD = 100;

function toCents(value: number): number {
  return Math.round(value * CENTS_PER_USD);
}

/**
 * Exact USD string from integer cents, trailing zeros trimmed: 15000 -> "150",
 * 30 -> "0.3", -500 -> "-5". Integer arithmetic only — dividing a float sum by
 * 100 is how 0.1 + 0.2 becomes 0.30000000000000004.
 */
export function centsToUsdString(cents: number): string {
  const whole = Math.trunc(cents);
  const sign = whole < 0 ? "-" : "";
  const abs = Math.abs(whole);
  const units = Math.floor(abs / CENTS_PER_USD);
  const remainder = abs % CENTS_PER_USD;
  if (remainder === 0) {
    return `${sign}${units}`;
  }
  return `${sign}${units}.${String(remainder).padStart(2, "0").replace(/0+$/, "")}`;
}

export function walletValuationValue(wallet: Wallet): number | null {
  return typeof wallet.balanceUsd === "number" && Number.isFinite(wallet.balanceUsd)
    ? wallet.balanceUsd
    : null;
}

export function summarizeWalletValuations(wallets: Wallet[]): ValuationSummary {
  let valuedTotalCents = 0;
  let valuedWalletCount = 0;
  let unvaluedWalletCount = 0;
  let staleWalletCount = 0;

  for (const wallet of wallets) {
    const value = walletValuationValue(wallet);
    if (value === null) {
      // A wallet with no valuation is counted, never added as zero: an unknown
      // balance and a real zero balance are different facts.
      unvaluedWalletCount += 1;
      continue;
    }
    valuedTotalCents += toCents(value);
    valuedWalletCount += 1;
    if (!wallet.lastSyncAgo) {
      staleWalletCount += 1;
    }
  }

  const status: ValuationStatus =
    valuedWalletCount === 0
      ? "UNVALUED"
      : unvaluedWalletCount > 0 || staleWalletCount > 0
        ? "PARTIAL"
        : "VALUED";

  return {
    valuedTotalUsd: valuedWalletCount > 0 ? centsToUsdString(valuedTotalCents) : null,
    valuedWalletCount,
    unvaluedWalletCount,
    staleWalletCount,
    asOf: null,
    status,
  };
}
